import { AxiosInstance } from 'axios';
import { ScenarioSuiteSummary } from './api';

/**
 * Uploading, replacing, downloading and deleting the suites of an environment.
 *
 * Written by hand next to the generated client, like the pre-flight call: the suite goes up as the file itself
 * rather than as a JSON body, because a suite may carry comments and a JSON body would refuse them. The API reads
 * the name out of the file and stores the suite under it.
 */

const suitesUrl = (customer: string, environment: string, suite?: string) =>
    `/ScenarioRun/${encodeURIComponent(customer)}/${encodeURIComponent(
        environment
    )}/suites${suite ? `/${encodeURIComponent(suite)}` : ''}`;

// The axios instance is created with transformResponse: [], so response.data is a string and has to be parsed here.
const parsed = <T>(data: unknown): T =>
    (typeof data === 'string' ? JSON.parse(data) : data) as T;

/** The reason the API gave for refusing, which is written to be shown as is. */
export const suiteFailureMessage = (error: any, fallback: string) => {
    const body = error?.response?.data;

    return typeof body === 'string' && body ? body : fallback;
};

/**
 * Stores a suite: a new one when `replacing` is left out, which the API refuses when the name is taken, or a new
 * version of `replacing`, which the file then has to carry the name of.
 */
export const saveScenarioSuite = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    content: string,
    replacing?: string
): Promise<ScenarioSuiteSummary> => {
    const response = await axios.request({
        method: replacing ? 'PUT' : 'POST',
        url: suitesUrl(customer, environment, replacing),
        data: content,
        headers: { 'Content-Type': 'text/plain' },
    });

    return parsed<ScenarioSuiteSummary>(response.data);
};

export const deleteScenarioSuite = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    suite: string
): Promise<void> => {
    await axios.delete(suitesUrl(customer, environment, suite));
};

/** Saves a suite as a file, exactly as it was uploaded, so it can be edited and uploaded again. */
export const downloadScenarioSuite = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    suite: string
): Promise<void> => {
    const response = await axios.get(suitesUrl(customer, environment, suite), {
        headers: { Accept: 'application/json' },
    });

    const content =
        typeof response.data === 'string'
            ? response.data
            : JSON.stringify(response.data, null, 2);

    const url = URL.createObjectURL(
        new Blob([content], { type: 'application/json' })
    );
    const link = document.createElement('a');

    link.href = url;
    link.download = `${suite}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
